import { useState, useCallback } from 'react'
import CheckboxGroup from '../../../react4xp/components/CheckboxGroup'
import FileInput from '../../../react4xp/components/FileInput'
import { validateFormInput } from '../../../lib/validation/form-validation.es6'
import { Button, Input, RadioButton, RadioGroup, Select, Text, Textarea } from '@postenbring/hedwig-react'

const KPForm = ({
    form,
    recaptchaSiteKey,
    recaptchaIsConfigured,
    errorMessages
}) => {
    const [inputs, setInputs] = useState(
        form?.inputs?.map((input) => ({ ...input, value: input.value })) || []
    )

    const [formSubmitted, setFormSubmitted] = useState()
    const [successMessage, setSuccessMessage] = useState()
    const [serverErrorMessage, setServerErrorMessage] = useState()

    const handleSubmitResponse = useCallback(async (response) => {
        const responseMessage = await response.json().then((res) => res.value)
        if (response?.status >= 400) {
            setServerErrorMessage(responseMessage ?? 'Error')
        } else {
            setSuccessMessage(responseMessage ?? 'Thank you!')
        }
        setFormSubmitted(true)
    }, [])

    const renderInputs = (inputs) => {
        const renderedInputs = inputs.map((input, index) => {
            if (input.type === 'hidden') {
                return (
                    <input
                        type='hidden'
                        name={input.name}
                        value={input.value}
                        key={index}
                    />
                )
            } else {
                return (
                    <div className="hds-mb-20-24" key={index}>
                        {renderInputType(input, index)}
                    </div>
                )
            }
        })
        return <>{renderedInputs}</>
    }

    const renderInputType = (input, index) => {
        if (input.type === 'textarea') {
            return (
                <Textarea
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'text') {
            return (
                <Input
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'email') {
            return (
                <Input
                    type='email'
                    label={input.label}
                    name={input.name}
                    value={input.value}
                    onChange={(e) => handleChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'select') {
            return (
                <Select
                    id={input.id}
                    label={input.label}
                    name={input.name}
                    onChange={(e) => handleDropDownChange(e)}
                    errorMessage={input.errorMessage}
                >
                    {input.options?.map((option, index) => (
                        <option
                            value={index === 0 ? "" : option.value}
                            key={index}
                            disabled={index === 0}
                            selected={index === 0}
                        >
                            {option.label}
                        </option>
                    ))}
                </Select>
            )
        } else if (input.type === 'radio') {
            if (!input.options || !Array.isArray(input.options)) {
                console.error('RadioButtons: options is not an array', options)
                return null
            }
            return (
                <RadioGroup legend={input.label} name={input.name} errorMessage={input.errorMessage}>
                    {input.options.map((option, index) => (
                        <RadioButton
                            value={option.value}
                            key={index}
                            onChange={handleRadioChange}
                        >
                            {option.label}
                        </RadioButton>
                    ))}

                </RadioGroup>
            )
        } else if (input.type === 'checkboxes') {
            return (
                <CheckboxGroup
                    name={input.name}
                    label={input.label}
                    options={input.options || []}
                    onChange={handleCheckboxChange}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'file') {
            return (
                <FileInput
                    name={input.name}
                    id={input.id}
                    label={input.label}
                    onChange={(e) => handleFileChange(e)}
                    errorMessage={input.errorMessage}
                />
            )
        } else if (input.type === 'header') {
            return <Text variant='h3'>{input.label}</Text>
        }
    }

    const validate = () => {
        let hasChanges = false
        const newInputList = inputs.map((input) => {
            if (!validateFormInput(input)) {
                input.errorMessage = errorMessages[input.type]
                hasChanges = true
            } else {
                if (input.errorMessage) {
                    delete input.errorMessage
                    hasChanges = true
                }
            }
            return input
        })

        if (hasChanges) {
            setInputs(newInputList)
        }
        return newInputList.filter((input) => input.errorMessage).length === 0 // no error messages
    }

    const handleCheckboxChange = ({ target }) => {
        let updated = false
        let updatedInputs = inputs.map((input) => {
            if (input.name === target.name && input.options) {
                let value = ''
                let updatedOptions = input.options.map((option) => {
                    if (option.value === target.value) {
                        option.checked = target.checked
                        updated = true
                    }
                    if (option.checked) {
                        value += option.value + ','
                    }
                    return option
                })
                if (updated) {
                    input.value = value.slice(0, -1)
                    input.options = updatedOptions
                    delete input.errorMessage
                }
            }
            return input
        })
        if (updated) {
            setInputs(updatedInputs)
        }
    }

    const handleRadioChange = ({ target }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === target.name && input.options) {
                    let updated = false
                    let updatedOptions = input.options.map((option) => {
                        if (option.value === target.value) {
                            option.checked = true
                            updated = true
                        } else {
                            option.checked = false
                        }
                        return option
                    })
                    if (updated) {
                        input.options = updatedOptions
                        input.value = target.value
                        delete input.errorMessage
                    }
                }
                return input
            })
        )
    }

    const handleFileChange = ({ target: { name, value, files } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    input.file = files[0]
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    const handleDropDownChange = ({ target: { name, value } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    const handleChange = ({ target: { name, value } }) => {
        setInputs(
            inputs.map((input) => {
                if (input.name === name) {
                    input.value = value
                    delete input.errorMessage
                }
                return input
            })
        )
    }

    /**
     * Reduce inputs array to object
     * Looks like
     * { email: 'person@company.no', name: 'Person' }
     * TODO: Should ignore subheaders
     */
    const inputsReducer = (obj, { name, value }) => ({ ...obj, [name]: value })

    const preSubmit = (event) => {
        validate()
    }

    const submit = (event) => {
        event.preventDefault()
        if (validate()) {
            if (recaptchaIsConfigured) {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(recaptchaSiteKey, { action: 'submit' })
                        .then((token) => {
                            let body = JSON.stringify(
                                inputs.reduce(inputsReducer, { token })
                            )

                            let formData = new FormData()

                            inputs.map((input) => {
                                if (input.type === 'file') {
                                    formData.append(input.name, input.file)
                                } else {
                                    formData.append(input.name, input.value)
                                }
                            })

                            formData.append('token', token)

                            fetch(form.actionUrl, {
                                method: 'POST',
                                body: formData
                            }).then(handleSubmitResponse)
                        })
                })
            } else {
                let formData = new FormData()

                inputs.map((input) => {
                    if (input.type === 'file') {
                        formData.append(input.name, input.file)
                    } else {
                        formData.append(input.name, input.value)
                    }
                })

                fetch(form.actionUrl, {
                    method: 'POST',
                    body: formData
                }).then(handleSubmitResponse)
            }
        }
    }

    return (
        <section>
            {formSubmitted ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: successMessage
                            ? successMessage
                            : serverErrorMessage
                    }}
                ></div>
            ) : (
                <form
                    id={form.id}
                    onSubmit={(event) => submit(event)}
                    method='POST'
                >
                    {renderInputs(inputs)}
                    <Button
                        type='submit'
                        onClick={(event) => preSubmit(event)}
                    >
                        {form.submitText}
                    </Button>
                </form>
            )}
        </section>
    )
}

export default (props) => <KPForm {...props} />
